'use client';

import React, { useState } from 'react';
import { Layers, Database, Play, Clock } from 'lucide-react';
import { colors, baseStyles } from './SharedUI';

// Hàm xử lý tiền tệ bao gồm cả số ÂM
const formatUSD = (val) => {
  if (val === null || val === undefined) return '-';
  const sign = val < 0 ? '-' : '';
  const absVal = Math.abs(val);

  if (absVal >= 1000000000) return `${sign}$${(absVal / 1000000000).toFixed(2)}B`;
  if (absVal >= 1000000) return `${sign}$${(absVal / 1000000).toFixed(2)}M`;
  if (absVal >= 1000) return `${sign}$${(absVal / 1000).toFixed(1)}k`;
  return `${sign}$${absVal.toFixed(0)}`;
};

// Format giá trị OHLC cho gọn (Ví dụ: 73500 -> 73.50k)
const formatPriceK = (price) => {
  if (price >= 1000) return `${(price / 1000).toFixed(2)}k`;
  return price.toFixed(2);
};

// Hàm lấy chuỗi Giờ:Phút (VD: 08:35)
const formatTime = (timestamp) => {
  const d = new Date(timestamp);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export default function TimeBoxedOrderFlow({ defaultSymbol = 'BTCUSDT' }) {
  const [symbol, setSymbol] = useState(defaultSymbol);
  const [slices, setSlices] = useState(12); // Lựa chọn băm: 8, 12, 24
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState([]);

  // Tách Base Asset để hiển thị tên coin (VD: BTCUSDT -> BTC)
  const baseAsset = symbol.toUpperCase().replace('USDT', '').replace('BUSD', '').replace('USDC', '');

  const handleExecute = async () => {
    if (!symbol.trim()) return;
    setIsLoading(true);

    try {
      const cleanSymbol = symbol.trim().toUpperCase();
      const NOW = Date.now();
      const slicePromises = [];

      // Vòng lặp băm lát cắt (i chạy từ slices lùi về 1)
      for (let i = slices; i >= 1; i--) {
        const endTime = NOW - (i - 1) * 60 * 60 * 1000;
        const startTime = endTime - 60 * 60 * 1000; // Chính xác 60 phút

        // Tạo tác vụ Fetch độc lập cho từng Block 1 Giờ (Sử dụng 5m limit 12)
        const fetchSliceData = async () => {
          const pKlines = fetch(`https://fapi.binance.com/fapi/v1/klines?symbol=${cleanSymbol}&interval=5m&limit=12&startTime=${startTime}&endTime=${endTime}`).then(r => r.json());
          const pOi = fetch(`https://fapi.binance.com/futures/data/openInterestHist?symbol=${cleanSymbol}&period=5m&limit=12&startTime=${startTime}&endTime=${endTime}`).then(r => r.json());
          //const pFund = fetch(`https://fapi.binance.com/fapi/v1/fundingRate?symbol=${cleanSymbol}&startTime=${startTime}&endTime=${endTime}`).then(r => r.json());
          
          const [klines, oiData] = await Promise.all([pKlines, pOi].map(p => p.catch(() => [])));
          const fundData = [];
          // 1. TỔNG HỢP KLINES (OHLC, SPREAD, RANGE, CVD)
          let open = 0, high = 0, low = 0, close = 0, vol = 0, cvd = 0, isGreen = false;
          let rangeVal = 0, rangePct = 0, spreadVal = 0, spreadPct = 0;

          if (Array.isArray(klines) && klines.length > 0) {
            open = parseFloat(klines[0][1]);
            close = parseFloat(klines[klines.length - 1][4]);
            high = Math.max(...klines.map(k => parseFloat(k[2])));
            low = Math.min(...klines.map(k => parseFloat(k[3])));
            
            // Tính toán Spread và Range
            rangeVal = high - low;
            rangePct = low > 0 ? (rangeVal / low) * 100 : 0;
            spreadVal = Math.abs(close - open);
            spreadPct = open > 0 ? (spreadVal / open) * 100 : 0;

            let totalQuoteVol = 0;
            let takerBuyQuoteVol = 0;
            klines.forEach(k => {
              totalQuoteVol += parseFloat(k[7]);
              takerBuyQuoteVol += parseFloat(k[10]);
            });
            vol = totalQuoteVol;
            cvd = takerBuyQuoteVol - (totalQuoteVol - takerBuyQuoteVol); // Mua - Bán
            isGreen = close >= open;
          }

          // 2. TỔNG HỢP OI (Cả Value USD và Số lượng Hợp đồng)
          let oiValueUsd = null;
          let oiAmountCoin = null;
          let oiStatus = '';
          
          if (Array.isArray(oiData) && oiData.length > 0 && !oiData.code) {
             const firstOiUsd = parseFloat(oiData[0].sumOpenInterestValue || 0);
             const lastOiUsd = parseFloat(oiData[oiData.length - 1].sumOpenInterestValue || 0);
             
             const lastOiAmt = parseFloat(oiData[oiData.length - 1].sumOpenInterest || 0);
             
             oiValueUsd = lastOiUsd; 
             oiAmountCoin = lastOiAmt;
             oiStatus = lastOiUsd > firstOiUsd ? 'Tăng' : (lastOiUsd < firstOiUsd ? 'Giảm' : 'Ngang');
          }

          // 3. TỔNG HỢP FUNDING
          let fundingRate = null;
          if (Array.isArray(fundData) && fundData.length > 0 && !fundData.code) {
             fundingRate = parseFloat(fundData[fundData.length - 1].fundingRate);
          }

          return {
            sliceLabel: `-${i}h trước`,
            isCurrentNow: i === 1,
            timeRange: `${formatTime(startTime)} - ${formatTime(endTime)}`,
            open, high, low, close, isGreen,
            rangeVal, rangePct, spreadVal, spreadPct,
            vol, cvd,
            isCvdPositive: cvd >= 0,
            oiUsd: oiValueUsd,
            oiAmount: oiAmountCoin,
            oiStatus,
            fundingRate
          };
        };

        slicePromises.push(fetchSliceData());
      }

      const results = await Promise.all(slicePromises);
      setTableData(results);

    } catch (error) {
      alert('Có lỗi xảy ra khi kéo dữ liệu. Vui lòng kiểm tra lại kết nối hoặc mã Token!');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ backgroundColor: colors.bg || '#0b0e14', color: '#ffffff', minHeight: '150px', padding: '20px', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: '1600px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        
        {/* KHỐI ĐIỀU KHIỂN CHÍNH */}
        <div style={{ ...baseStyles.card, display: 'flex', flexDirection: 'column', gap: '16px', padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Layers size={24} color={colors.blue || '#3b82f6'} />
              <div>
                <h1 style={{ margin: 0, fontSize: '20px', color: '#ffffff', fontWeight: 'bold' }}>TIME-BOXED ORDER FLOW</h1>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: colors.textSub, fontWeight: '700' }}>
                  Phân tích chuỗi dòng tiền bằng lát cắt tĩnh (Gộp 12 nến 5m/Lát)
                </p>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input 
                type="text" 
                value={symbol} 
                onChange={(e) => setSymbol(e.target.value)} 
                placeholder="Ví dụ: BTCUSDT"
                style={{ ...baseStyles.input, width: '120px', textTransform: 'uppercase', color: '#ffffff', fontWeight: 'bold' }} 
                disabled={isLoading}
              />
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: 'rgba(255,255,255,0.05)', padding: '6px 12px', borderRadius: '6px', border: `1px solid ${colors.border}` }}>
                <span style={{ fontSize: '12px', color: '#ffffff', fontWeight: 'bold' }}>BĂM LÁT CẮT:</span>
                <select 
                  value={slices} 
                  onChange={(e) => setSlices(Number(e.target.value))}
                  disabled={isLoading}
                  style={{ backgroundColor: 'transparent', color: '#fbbf24', border: 'none', fontWeight: 'bold', fontSize: '14px', outline: 'none', cursor: 'pointer' }}
                >
                  <option value={8} style={{color: '#000'}}>8 Giờ (8 Lát)</option>
                  <option value={12} style={{color: '#000'}}>12 Giờ (12 Lát)</option>
                  <option value={24} style={{color: '#000'}}>24 Giờ (24 Lát)</option>
                </select>
              </div>

              <button 
                onClick={handleExecute} 
                disabled={isLoading} 
                style={{ ...baseStyles.select, backgroundColor: isLoading ? colors.border : '#65a30d', color: '#ffffff', border: 'none', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                {isLoading ? <Clock size={16} /> : <Play size={16} />} 
                {isLoading ? 'ĐANG KÉO DATA...' : 'PHÂN TÍCH SUMMARY'}
              </button>
            </div>
          </div>
        </div>

        {/* BẢNG DỮ LIỆU TÓM TẮT (SUMMARY DATA MATRIX) */}
        {tableData.length > 0 && (
          <div style={{ ...baseStyles.card, padding: '20px', border: `1px solid ${colors.border}`, background: 'linear-gradient(180deg, #0f172a 0%, #020617 100%)' }}>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{ fontSize: '15px', color: '#fbbf24', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Database size={18} /> SUMMARY MATRIX DÒNG TIỀN (CHUỖI {slices} GIỜ QUA)
              </strong>
              <span style={{ fontSize: '12px', color: colors.textSub, fontStyle: 'italic' }}>*Dữ liệu mỗi lát cắt được tổng hợp từ 12 nến 5 phút</span>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', whiteSpace: 'nowrap' }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${colors.border}`, backgroundColor: 'rgba(0,0,0,0.4)' }}>
                    <th style={{ padding: '14px 10px', fontSize: '13px', color: colors.textSub, fontWeight: 'bold' }}>Lát cắt (Giờ)</th>
                    <th style={{ padding: '14px 10px', fontSize: '13px', color: colors.textSub, fontWeight: 'bold' }}>Khung Giờ thực tế</th>
                    <th style={{ padding: '14px 10px', fontSize: '13px', color: colors.textSub, fontWeight: 'bold' }}>OHLC (Màu nến)</th>
                    <th style={{ padding: '14px 10px', fontSize: '13px', color: colors.textSub, fontWeight: 'bold' }}>Spread / Range</th>
                    <th style={{ padding: '14px 10px', fontSize: '13px', color: colors.textSub, fontWeight: 'bold' }}>Volume (1h)</th>
                    <th style={{ padding: '14px 10px', fontSize: '13px', color: colors.textSub, fontWeight: 'bold' }}>Mua/Bán (CVD 1h)</th>
                    <th style={{ padding: '14px 10px', fontSize: '13px', color: colors.textSub, fontWeight: 'bold' }}>Open Interest (USD/Coin)</th>
                    <th style={{ padding: '14px 10px', fontSize: '13px', color: colors.textSub, fontWeight: 'bold' }}>Funding Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, i) => (
                    <tr 
                      key={`slice-${i}`} 
                      style={{ 
                        borderBottom: `1px solid rgba(255,255,255,0.05)`, 
                        backgroundColor: row.isCurrentNow ? 'rgba(59, 130, 246, 0.1)' : 'transparent', 
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = row.isCurrentNow ? 'rgba(59, 130, 246, 0.1)' : 'transparent'}
                    >
                      {/* Lát cắt */}
                      <td style={{ padding: '14px 10px', fontSize: '14px', fontWeight: 'bold', color: '#e2e8f0' }}>
                        {row.sliceLabel}
                        {row.isCurrentNow && <span style={{ marginLeft: '8px', fontSize: '10px', backgroundColor: colors.blue, padding: '2px 6px', borderRadius: '4px' }}>NOW</span>}
                      </td>
                      
                      {/* Khung giờ */}
                      <td style={{ padding: '14px 10px', fontSize: '13px', color: '#94a3b8', fontFamily: 'monospace' }}>
                        {row.timeRange}
                      </td>

                      {/* OHLC */}
                      <td style={{ padding: '14px 10px', fontSize: '13px', fontFamily: 'monospace' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ color: row.isGreen ? colors.green : colors.red, fontWeight: 'bold', fontSize: '14px' }}>
                            {row.isGreen ? '🟢 Tăng' : '🔴 Giảm'}
                          </span>
                          <span style={{ color: colors.textSub, fontSize: '12px' }}>
                            (O:{formatPriceK(row.open)}, H:{formatPriceK(row.high)}, L:{formatPriceK(row.low)}, C:<strong style={{ color: row.isGreen ? colors.green : colors.red }}>{formatPriceK(row.close)}</strong>)
                          </span>
                        </div>
                      </td>

                      {/* Spread / Range */}
                      <td style={{ padding: '14px 10px', fontSize: '13px', fontFamily: 'monospace' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span style={{ color: row.isGreen ? colors.green : colors.red }} title="Spread: Mức độ giá di chuyển thực tế (Đóng cửa - Mở cửa)">
                            S: {formatPriceK(row.spreadVal)} ({row.spreadPct.toFixed(2)}%)
                          </span>
                          <span style={{ color: '#fbbf24' }} title="Range: Biên độ dao động tối đa trong giờ (Cao nhất - Thấp nhất)">
                            R: {formatPriceK(row.rangeVal)} ({row.rangePct.toFixed(2)}%)
                          </span>
                        </div>
                      </td>

                      {/* Volume 1h */}
                      <td style={{ padding: '14px 10px', fontSize: '14px', fontWeight: 'bold', color: '#f8fafc', fontFamily: 'monospace' }}>
                        {formatUSD(row.vol)}
                      </td>

                      {/* Net CVD */}
                      <td style={{ padding: '14px 10px', fontSize: '14px', fontWeight: 'bold', fontFamily: 'monospace' }}>
                        <span style={{ color: row.isCvdPositive ? colors.green : colors.red }}>
                          {row.isCvdPositive ? '+' : ''}{formatUSD(row.cvd)}
                        </span>
                        <span style={{ fontSize: '11px', color: colors.textSub, marginLeft: '6px', fontWeight: 'normal', fontFamily: 'sans-serif' }}>
                          ({row.isCvdPositive ? 'Mua' : 'Bán'})
                        </span>
                      </td>

                      {/* Open Interest (Usd + Số Lượng Coin) */}
                      <td style={{ padding: '14px 10px', fontSize: '13px', fontFamily: 'monospace' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span style={{ fontWeight: 'bold', color: '#e2e8f0', fontSize: '14px' }}>
                            {formatUSD(row.oiUsd)}
                            {row.oiStatus && (
                              <span style={{ 
                                fontSize: '11px', marginLeft: '6px', fontFamily: 'sans-serif', fontWeight: 'bold',
                                color: row.oiStatus === 'Tăng' ? colors.green : (row.oiStatus === 'Giảm' ? colors.red : colors.textSub) 
                              }}>
                                ({row.oiStatus})
                              </span>
                            )}
                          </span>
                          <span style={{ color: colors.textSub }} title={`Số lượng hợp đồng (${baseAsset})`}>
                            {row.oiAmount !== null ? `${row.oiAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} ${baseAsset}` : '-'}
                          </span>
                        </div>
                      </td>

                      {/* Funding Rate */}
                      <td style={{ padding: '14px 10px', fontSize: '14px', fontWeight: 'bold', fontFamily: 'monospace' }}>
                        {row.fundingRate !== null ? (
                          <span style={{ color: row.fundingRate > 0 ? colors.green : (row.fundingRate < 0 ? colors.red : '#94a3b8') }}>
                            {(row.fundingRate * 100).toFixed(4)}%
                          </span>
                        ) : (
                          <span style={{ color: '#475569' }}>-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
